import clsx from "clsx";
import { useTranslation } from "react-i18next";

import type { SymbolOverride } from "../../data";
import type { WizardPartner } from "../useCreateProgramWizard";

import { BaseInfoIcon } from "../../../../Icons";
import { InlineNotification } from "../../../../InlineNotification";
import { TooltipWrapper } from "../../../../Tooltip";
import { Typo } from "../../../../Typo";
import { formatOverridePcts } from "../../RebateSplitOverride/helpers";
import { SubIbStatusTag } from "../../SubIbStatusTag";

type ReviewStepProps = {
  title: string;
  stepLabel?: string;
  partners: WizardPartner[];
  masterShare: number;
  submitError: boolean;
  isMobile?: boolean;
  /** The Comment row is Phase-2 (part-4039-master-sub-ib-phase-2) — hidden while the flag is off. */
  showComment?: boolean;
  /**
   * The Master IB's own rebate account, where a newly created program's
   * rebate earnings are paid. Auto-resolved (no wizard step); surfaced
   * read-only here. Null while the resource that provides it is unavailable
   * or still loading.
   */
  masterRebateAccount: string | null;
  /**
   * Rules configured on the Split step's override sub-page. Read-only here,
   * between the default split and the rebate account — matches the canvas's
   * `OverrideReviewBlock` (`SymbolOverride.tsx`), which sits in that same
   * position. Omitted rows when empty: an override is opt-in, so most
   * programs show nothing here.
   */
  overrides?: SymbolOverride[];
  /** masterSubIbPhase2: show the Pending tag under a pending-KYC Sub IB's name. */
  phase2Enabled?: boolean;
};

export const ReviewStep = ({
  title,
  stepLabel,
  partners,
  masterShare,
  submitError,
  isMobile,
  showComment,
  masterRebateAccount,
  overrides = [],
  phase2Enabled,
}: ReviewStepProps) => {
  const { t } = useTranslation();

  const splitSummary = [masterShare, ...partners.map((p) => p.splitPct)]
    .map((n) => `${n}%`)
    .join(" / ");

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="flex flex-col gap-3">
        {stepLabel && (
          <Typo
            element="span"
            type="labelXS"
            textColor="text-text-tertiary"
            className="uppercase tracking-[0.04em]"
          >
            {stepLabel}
          </Typo>
        )}
        <Typo element="h2" type={isMobile ? "headingLG" : "headingXL"}>
          {title}
        </Typo>
        <Typo element="p" type="bodySM" textColor="text-text-secondary">
          {partners.length > 1
            ? t(
                "subIBReport_reviewNoticeTwoSub",
                "Once confirmed, both Sub IBs will be notified and you can all start referring clients immediately via the new referral link.",
              )
            : t(
                "subIBReport_reviewSubtitle",
                "Once confirmed, the Sub IB will be notified and you can both start referring clients immediately via the new referral link.",
              )}
        </Typo>
      </div>

      {/* The step root's `gap-6` already puts 24px between this block and the
          notification below it, but every review row carries its own `py-4`,
          so the last row's 16px bottom padding read as a 40px gap (design QA
          measured exactly that). Dropping the trailing padding leaves the
          24px the root gap provides. Applied at the container rather than on
          a specific row because which row lands last depends on the optional
          Comment / overrides rows. */}
      <div className="flex flex-col gap-2 [&>*:last-child]:pb-0">
        <div className="flex flex-col">
          {partners.map((partner, i) => (
            <div
              key={partner.partnerId}
              className={`flex flex-col gap-4 py-4 ${i > 0 ? "border-t border-stroke-primary" : "pt-0"}`}
            >
              <div className="grid grid-cols-[150px_1fr] items-start gap-4">
                <Typo
                  element="span"
                  type="labelSM"
                  textColor="text-text-secondary"
                >
                  {partners.length > 1
                    ? t("subIBReport_reviewSubIbN", "Sub IB {{n}}", {
                        n: i + 1,
                      })
                    : t("subIBReport_reviewSubIb", "Sub IB")}
                </Typo>
                <div className="flex min-w-0 flex-col items-end gap-0.5 text-right">
                  <Typo
                    element="span"
                    type={isMobile ? "bodySM" : "bodyMD"}
                    className="w-full truncate"
                  >
                    {partner.displayName}
                  </Typo>
                  {phase2Enabled && partner.kycStatus === "Pending" && (
                    <SubIbStatusTag status="pendingKYC" isMobile={isMobile} />
                  )}
                  <Typo
                    element="span"
                    type="bodySM"
                    textColor="text-text-secondary"
                    className="w-full truncate"
                  >
                    {partner.email}
                  </Typo>
                </div>
              </div>
              <div className="grid grid-cols-[150px_1fr] items-start gap-4">
                <Typo
                  element="span"
                  type="labelSM"
                  textColor="text-text-secondary"
                >
                  {t("subIBReport_permIdentityTitle", "Show name and email")}
                </Typo>
                <Typo
                  element="span"
                  type={isMobile ? "bodySM" : "bodyMD"}
                  className="text-right"
                >
                  {partner.identityToggle
                    ? t("common_on", "On")
                    : t("common_off", "Off")}
                </Typo>
              </div>
              {showComment && (
                <div className="grid grid-cols-[150px_1fr] items-start gap-4">
                  <Typo
                    element="span"
                    type="labelSM"
                    textColor="text-text-secondary"
                  >
                    {t("subIBReport_reviewComment", "Note")}
                  </Typo>
                  <Typo
                    element="span"
                    type={isMobile ? "bodySM" : "bodyMD"}
                    textColor={
                      partner.comment.trim()
                        ? "text-text-primary"
                        : "text-text-tertiary"
                    }
                    className="text-right"
                  >
                    {partner.comment.trim()
                      ? partner.comment
                      : t("subIBReport_reviewNoComment", "None")}
                  </Typo>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-[150px_1fr] items-start gap-4 border-t border-stroke-primary py-4">
          <Typo element="span" type="labelSM" textColor="text-text-secondary">
            {t("subIBReport_reviewSplit", "Default rebate split")}
          </Typo>
          <div className="flex flex-col items-end gap-0.5 text-right">
            <Typo element="span" type={isMobile ? "bodySM" : "bodyMD"}>
              {splitSummary}
            </Typo>
            <Typo element="span" type="bodyXS" textColor="text-text-tertiary">
              {partners.length > 1
                ? t(
                    "subIBReport_reviewSplitCaptionTwoSub",
                    "You / Sub IB 1 / Sub IB 2",
                  )
                : t("subIBReport_reviewSplitCaption", "You / Sub IB")}
            </Typo>
          </div>
        </div>

        {overrides.length > 0 && (
          <div className="grid grid-cols-[150px_1fr] items-start gap-4 pb-4">
            <Typo element="span" type="labelSM" textColor="text-text-secondary">
              {t("subIBReport_rebateSplitOverride", "Override rules")}
            </Typo>
            <div
              className={clsx(
                "flex flex-col items-end text-right",
                isMobile ? "gap-2" : "gap-0.5",
              )}
            >
              {overrides.map((rule, i) => (
                <div
                  key={`${rule.scopeType}-${rule.scopeValue}-${i}`}
                  className={clsx(
                    "flex",
                    isMobile
                      ? "flex-col items-end gap-0.5"
                      : "items-baseline gap-2",
                  )}
                >
                  <Typo element="span" type={isMobile ? "bodySM" : "bodyMD"}>
                    {rule.scopeValue}
                  </Typo>
                  <Typo element="span" type={isMobile ? "bodySM" : "bodyMD"}>
                    {formatOverridePcts(rule)}
                  </Typo>
                </div>
              ))}
              <Typo element="span" type="bodyXS" textColor="text-text-tertiary">
                {partners.length > 1
                  ? t(
                      "subIBReport_reviewSplitCaptionTwoSub",
                      "You / Sub IB 1 / Sub IB 2",
                    )
                  : t("subIBReport_reviewSplitCaption", "You / Sub IB")}
              </Typo>
            </div>
          </div>
        )}

        <div className="grid grid-cols-[150px_1fr] items-start gap-4 border-t border-stroke-primary py-4">
          <span className="inline-flex items-center gap-1 text-text-secondary">
            <Typo element="span" type="labelSM" textColor="text-text-secondary">
              {t("subIBReport_reviewRebateAccount", "Rebate account")}
            </Typo>
            <TooltipWrapper
              id="review-rebate-account-info"
              text={t(
                "subIBReport_reviewRebateAccountInfo",
                "Rebate program earnings are paid to a rebate account",
              )}
              cursor="help"
              color="blue"
              tooltipMaxWidth={240}
              tooltipContainerClassName="!text-xs"
              tooltipPlacement="top"
            >
              <BaseInfoIcon
                height={14}
                width={14}
                className="text-icon-tertiary"
              />
            </TooltipWrapper>
          </span>
          <Typo
            element="span"
            type={isMobile ? "bodySM" : "bodyMD"}
            className="text-right"
          >
            {masterRebateAccount ?? "—"}
          </Typo>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {/* The split is fixed at creation, and that is true of one Sub IB and of
            two alike — so this carries no per-count variant, unlike the subtitle
            above. */}
        <InlineNotification variant="info">
          {t(
            "subIBReport_reviewNoticeInfo",
            "Once created, this program's rebate split cannot be changed",
          )}
        </InlineNotification>

        {submitError && (
          <InlineNotification variant="error">
            {t(
              "subIBReport_submitError",
              "Unable to create rebate program. Please try again, or contact support@pepperstonepartners.com if this problem persists.",
            )}
          </InlineNotification>
        )}
      </div>
    </div>
  );
};
